import React from "react";
import { Box, IconButton, Link, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import AppCache from "../../Helper/Common/AppCache";
import {
  getTypeConfig,
  getVisibleNotifications,
  DISMISSED_NOTIFICATIONS_CACHE_KEY,
} from "./notificationConfig";

/**
 * Renders org-meta-driven on-screen notices (warning / error / notice / info /
 * success / maintenance). Content is entirely controlled by org meta — this
 * component is presentational and renders nothing when there is nothing to show.
 *
 * @param {Array} notifications  array of notification objects from page/org meta
 */
const OrgNotificationBanner = ({ notifications }) => {
  const [dismissedIds, setDismissedIds] = React.useState(
    () => AppCache.getItem(DISMISSED_NOTIFICATIONS_CACHE_KEY) || []
  );

  const visible = React.useMemo(
    () => getVisibleNotifications(notifications, { now: dayjs().valueOf(), dismissedIds }),
    [notifications, dismissedIds]
  );

  const handleDismiss = (id) => {
    if (!id) return;
    const next = Array.from(new Set([...dismissedIds, id]));
    setDismissedIds(next);
    AppCache.setItem(DISMISSED_NOTIFICATIONS_CACHE_KEY, next, true);
  };

  if (!visible.length) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
      {visible.map((notification, index) => {
        const config = getTypeConfig(notification.type);
        const Icon = config.icon;
        return (
          <Box
            key={notification.id || index}
            data-testid={`org-notification-${notification.type || "info"}`}
            role="status"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              padding: "12px 16px",
              borderRadius: "10px",
              backgroundColor: config.background,
              border: `1px solid ${config.border}`,
            }}
          >
            <Icon sx={{ fontSize: 20, color: config.iconColor, flexShrink: 0, mt: "1px" }} />
            <Typography
              variant="fw400s14c000000"
              sx={{ flexGrow: 1, color: config.textColor, lineHeight: "20px" }}
            >
              {notification.title && (
                <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                  {notification.title}
                </Box>
              )}
              {notification.message}
              {notification.link?.url && notification.link?.label && (
                <Link
                  href={notification.link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ml: 0.75, color: config.iconColor, fontWeight: 600 }}
                >
                  {notification.link.label}
                </Link>
              )}
            </Typography>
            {notification.dismissible && notification.id && (
              <IconButton
                size="small"
                aria-label="Dismiss notification"
                data-testid="org-notification-dismiss"
                onClick={() => handleDismiss(notification.id)}
                sx={{ p: 0.25, color: config.textColor, flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default OrgNotificationBanner;
